/**
 * Phase 1D.9 — SimpliMeta root-tenant detection.
 *
 * The "root" tenant is the SimpliMeta-internal tenant
 * (`ten_simplimeta_root` in the backend, see `seeds/seed_tenants.py`).
 * Some UI elements — hardcoded mock/showcase AI Employee cards, demo
 * stat tiles — are meaningful for SimpliMeta-internal users as
 * walkthrough content, but actively confusing for partner/account
 * tenants who see them next to their real instances.
 *
 * This module provides a single source of truth for that check so we
 * don't sprinkle string comparisons through the codebase. If the
 * constant ever changes (it shouldn't), this is the one file to edit.
 *
 * Mirrors `backend/seeds/seed_tenants.py::SIMPLIMETA_ROOT_TENANT_ID`.
 */

import { useAuth } from '../contexts/AuthContext';

export const SIMPLIMETA_ROOT_TENANT_ID = 'ten_simplimeta_root';

/**
 * True iff the user belongs to the SimpliMeta root tenant.
 *
 * @param {{ tenant_id?: string } | null | undefined} user
 * @returns {boolean}
 */
export function isRootTenantUser(user) {
  return !!user && user.tenant_id === SIMPLIMETA_ROOT_TENANT_ID;
}

/**
 * React hook — returns true iff the current authenticated user is on
 * the SimpliMeta root tenant. Returns false during auth loading or
 * when logged out, so callers can use it directly as a render gate
 * without an extra loading branch (the gated content will simply not
 * appear until auth resolves; for non-root tenants it never appears).
 *
 * @returns {boolean}
 */
export function useIsRootTenant() {
  const { user } = useAuth();
  return isRootTenantUser(user);
}

/**
 * Whether to draw the SimpliMeta sidebar rather than a tenant one.
 *
 * Phase 2E.2 — raised by Aidan on 13 Oct 2026: impersonating AppDirect
 * still showed the whole SimpliMeta menu.
 *
 * `isRootTenantUser` answers "is this a SimpliMeta ACCOUNT", which is not
 * the question the sidebar wants. Impersonation state lives in
 * sessionStorage and travels as a request header; it never touches the
 * user object, so the account stays root while every API response on the
 * page is the tenant's. The sidebar needs "am I ACTING AS SimpliMeta
 * right now".
 *
 * Kept here rather than inline in Layout so the rule has a name and one
 * home. isRootTenantUser is unchanged — plenty of callers legitimately
 * ask about the account itself.
 *
 * Narrowing is safe: ImpersonationBanner renders above the sidebar and
 * carries an Exit control, so losing the Tenants entry cannot strand a
 * root admin mid-impersonation.
 *
 * @param {boolean} isRoot — from isRootTenantUser / useIsRootTenant
 * @param {boolean} impersonating — from isImpersonating()
 * @returns {boolean}
 */
export function shouldShowRootNav(isRoot, impersonating) {
  return !!isRoot && !impersonating;
}
